import React, { useEffect, useState } from "react";
import { Phone, Video, MessageCircle, Calendar, CreditCard, Receipt, Globe, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardContent, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Header } from "../components/ui/header";
import { Footer } from "../components/ui/footer";
import { money } from "../utilities/currency";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getAgentDisplayPicUrl } from "../utilities/conversions";
import { useSelector } from "react-redux";
import { RootState } from "../state/store";

/* --- Skill details modal --- */
function SkillModal({ skill, onClose }) {
    useEffect(() => {
        function onEsc(e) { if (e.key === 'Escape') onClose(); }
        window.addEventListener('keydown', onEsc);
        return () => window.removeEventListener('keydown', onEsc);
    }, [onClose]);

    if (!skill) return null;
    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full relative" onClick={(e) => e.stopPropagation()}>
                <button aria-label="Lukk" onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700">
                    <X />
                </button>
                <h2 className="text-xl font-bold mb-3">{skill.label}</h2>
                {skill.image ? (
                    <img src={skill.image} alt={skill.label} className="w-full h-40 object-cover rounded-lg mb-4" />
                ) : null}
                <p className="text-gray-700 leading-relaxed">{skill.description}</p>
            </div>
        </div>
    );
}

/* --- Booking selector (Vipps-style) --- */
function BookingSelector({ agent, onClose }) {
    const [month, setMonth] = useState("October");
    const [day, setDay] = useState("22");
    const [time, setTime] = useState("");
    const [duration, setDuration] = useState("");
    const [countryCode, setCountryCode] = useState("+47");
    const [phone, setPhone] = useState("");
    const [type, setType] = useState("call");

    const handleBooking = () => {
        alert(`Booking confirmed for ${agent.name} on ${month} ${day} at ${time}, ${duration}, ${type} via Vipps.`);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full relative">
                <button onClick={onClose} className="absolute right-4 top-4 text-gray-500">✕</button>
                <h2 className="text-xl font-semibold mb-4">Book an appointment with {agent.name}</h2>

                <div className="grid grid-cols-3 gap-2 mb-3">
                    <select value={month} onChange={(e) => setMonth(e.target.value)} className="border rounded-lg p-2">
                        <option>October</option>
                        <option>November</option>
                        <option>December</option>
                    </select>
                    <select value={day} onChange={(e) => setDay(e.target.value)} className="border rounded-lg p-2">
                        {Array.from({ length: 31 }, (_, i) => (
                            <option key={i + 1}>{i + 1}</option>
                        ))}
                    </select>
                    <select value={time} onChange={(e) => setTime(e.target.value)} className="border rounded-lg p-2">
                        {Array.from({ length: 24 }, (_, i) => (
                            <option key={i}>{`${String(i).padStart(2, "0")}:00`}</option>
                        ))}
                    </select>
                </div>

                <div className="mb-4">
                    <label className="font-semibold text-sm block mb-1">Duration</label>
                    <select value={duration} onChange={(e) => setDuration(e.target.value)} className="w-full border rounded-lg p-2">
                        <option value="">Select duration</option>
                        <option>5 minutes</option>
                        <option>10 minutes</option>
                        <option>15 minutes</option>
                        <option>20 minutes</option>
                        <option>30 minutes</option>
                        <option>45 minutes</option>
                        <option>60 minutes</option>
                    </select>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                    <div>
                        <label className="font-semibold text-sm block mb-1">Country Code</label>
                        <Input value={countryCode} onChange={(e) => setCountryCode(e.target.value)} />
                    </div>
                    <div>
                        <label className="font-semibold text-sm block mb-1">Phone Number</label>
                        <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                    </div>
                </div>

                <div className="mb-4">
                    <label className="font-semibold text-sm block mb-2">Type</label>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={() => setType("call")}
                            className={`border rounded-lg flex items-center justify-center p-3 transform transition-all duration-300 hover:scale-105 hover:shadow-lg ${type === "call" ? "bg-orange-50 border-orange-400" : "border-gray-300"
                                }`}
                        >
                            <Phone className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setType("video")}
                            className={`border rounded-lg flex items-center justify-center p-3 transform transition-all duration-300 hover:scale-105 hover:shadow-lg ${type === "video" ? "bg-orange-50 border-orange-400" : "border-gray-300"
                                }`}
                        >
                            <Video className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <Button className="bg-[#FF5B24] w-full py-6 text-lg font-semibold transform transition-all duration-300 hover:scale-105 hover:shadow-xl" onClick={handleBooking}>
                    Continue with <span className="ml-2 font-bold">Vipps</span>
                </Button>
            </div>
        </div>
    );
}

// const agent = {
//     name: "Victor Norman",
//     title: "Rådgiver – Bedriftsjuss",
//     image: "https://images.unsplash.com/photo-1607746882042-944635dfe10e?q=80&w=400&auto=format&fit=crop",
//     bio: `Praktisk, løsningsorientert rådgiver. 1000+ samtaler siden 2023. Spesialisert innen bedriftsjuss, restrukturering og bærekraftige forretningsstrategier. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec accumsan justo sed sapien vulputate, vitae suscipit felis pretium. Morbi in sodales metus. Vivamus sed justo ut metus vulputate bibendum. Curabitur posuere, leo sit amet finibus tincidunt, mi metus bibendum velit, sed laoreet enim risus id purus. Integer sagittis libero eu neque viverra, nec hendrerit nulla iaculis. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae; Donec malesuada erat nec efficitur interdum. Nam id justo sit amet enim posuere gravida. Nulla facilisi. Suspendisse potenti. Donec pretium, lacus et tincidunt pretium, urna lacus laoreet justo, eget feugiat risus elit a nunc.`,
//     skills: [
//         { icon: <CreditCard size={18} />, label: "Bank og finansiering", image: "https://images.unsplash.com/photo-1565514027011-6c0b97ff1c2a?q=80&w=600&auto=format&fit=crop", description: "Rådgivning innen kredittvurdering, kapitalstruktur og finansieringsstrategier for små og mellomstore bedrifter." },
//         { icon: <Receipt size={18} />, label: "Insolvens og restrukturering", image: "https://images.unsplash.com/photo-1581090464777-7c8f9b4b1a6b?q=80&w=600&auto=format&fit=crop", description: "Veiledning i omstrukturering, konkursforebygging og gjeldsforhandlinger." },
//         { icon: <Globe size={18} />, label: "Bærekraft og klimarisiko", image: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?q=80&w=600&auto=format&fit=crop", description: "Strategisk rådgivning om ESG, grønn finansiering og bærekraftige investeringer." },
//     ],
// };


type AgentProfilePageProps = {
    currentUser: any,
    onLogout: any,
    baseUrl: string,
    onLoginClick: any
}

const T = {
    offer: "✨ Spesialtilbud – Betal 99 kr, få 198 kr i ringe kreditt! ✨",
    signIn: "Logg inn",
    join: "Bli med",
    allAdvisors: "Alle veiledere",
    festivalTitle: "Festivalkampanjer",
    referTitle: "Verv & få gratis reading!",
    referBody: "Verv 5 betalende kunder og få en GRATIS 15‑min reading hos en toppveileder.",
    blogTitle: "Fra Mystic‑bloggen",
    viewAll: "Se alle",
    subscribeLine: "Få nye guider og tilbud på e‑post.",
    read: "Les",
    invite: "Inviter venner",
    sponsored: "Sponset",
    ad: (n: number) => `Annonse ${n}`,
    adBlurb: "Annonser produkter, apper eller arrangementer her.",
    learnMore: "Les mer",
    connectNow: "Koble til nå",
    from: "fra",
    minutes: "min",
    verified: "Verifisert",
    categories: [
        "Kjærlighetsreading",
        "Klarsynt reading",
        "Tarot‑reading",
        "Astrologi & Horoskop",
        "Englebudskap",
        "Orakel‑veiledning",
        "Drømmetydning",
        "Åndelig medium",
    ],
    hero: {
        name: "Klarsynte Eirik",
        tag: "Kjærlighetsekspert",
        blurb:
            "Mitt løfte: Jeg er her for deg. Når det er vanskelig å vite hvem man kan stole på, er ekte nærvær alt.",
        getReading: "Bestill reading",
    },
    footer: {
        getApp: "Få appen for klarhet hvor du enn er.",
        explore: "Utforsk",
        special: "Spesialiteter",
        more: "Mer",
        advisors: "Veiledere",
        blog: "Bloggen vår",
        applyCode: "Bruk kampanjekode",
        support: "Kundesupport",
        about: "Om oss",
        apply: "Veileder? Søk her",
        privacy: "Personvern",
        terms: "Vilkår",
    },
    wallet: {
        title: "Kjøp ringeminutter",
        intro:
            "Minutter lagres i din Mystic‑wallet og kan brukes hos alle veiledere (chat / samtale / video).",
        numLabel: "Telefonnummeret du ringer fra",
        amount: "Velg beløp",
        buy: (amt: number) => `Kjøp ${money(amt)} kreditt`,
        learn: "Les mer om betalingsmåter",
        err: "Skriv inn et gyldig telefonnummer",
        otp: (n: string) => `Kode sendt til ${n}. Skriv den inn for å bekrefte.`,
        enterOtp: "Skriv inn kode",
        verify: "Bekreft & legg til kreditt",
        cancel: "Avbryt",
        success: (amt: number) => `${money(amt)} lagt til i lommeboken!`,
        pm1: "Vipps MobilePay",
        pm2: "Kort (krever registrering på ditt mobilnummer)",
        nolgin: "Ingen innlogging nødvendig for Vipps MobilePay – ring direkte.",
    },
};

export default function AgentProfilePage({ currentUser, onLogout, baseUrl, onLoginClick }: AgentProfilePageProps) {
    const [showBooking, setShowBooking] = useState(false);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [wallet, setWallet] = useState(0);
    const { agentId } = useParams<{ agentId: string }>();
    const [loading, setLoading] = useState(true);
    const client = useSelector((state: RootState) => state.client.client);

    const [agent, setAgent] = useState<any>(null);

    const fetchAgent = async () => {
        try {
            const response = await axios.get(baseUrl + `/getAgentByUID`, {
                params: { code: agentId, client_uid: client.uid },
            });

            if (response.data.status === "OK") {
                const agentData = response.data.data;

                // ✅ Ensure image URL
                agentData.image = getAgentDisplayPicUrl(agentData.display_pic, baseUrl);
                // ✅ Normalize skills (handles single number, string, or array)
                let skillIds = agentData.skills;
                if (!Array.isArray(skillIds)) {
                    skillIds = [skillIds];
                }

                // ✅ Filter matching skill objects
                agentData.skills = (agentData.skills_list || []).filter(s =>
                    skillIds.includes(String(s.skill_Id))
                );
                setAgent(agentData);
            } else {
                setAgent(null);
            }
        } catch (error) {
            console.error("Error fetching agent:", error);
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAgent();
    }, [agentId, client]);


    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        if (currentUser?.role === "customer") {
            axios.get(baseUrl + "/getCustomerWallet", { params: { token: currentUser.token } })

                .then((response) => {
                    if (response.data.status === "OK") {
                        setWallet(response.data.data.remaining_amount);
                    }
                });
        }
    }, [currentUser, baseUrl])

    return (
        <div className={"bg-white text-gray-900"}>
            <Header wallet={wallet} scrolled={scrolled} T={T} onLoginClick={onLoginClick} currentUser={currentUser} onLogout={onLogout} />
            {loading ? (
                <div className="text-center py-20 text-gray-500 text-lg">
                    Loading agent details...
                </div>
            ) : !agent ? (
                <div className="text-center py-20 text-red-500 text-lg">
                    Agent not found
                </div>
            ) : (<div className="max-w-5xl mx-auto py-10 px-4">
                <div className="flex flex-col md:flex-row items-start gap-6 mb-8">
                    <img src={agent.image} alt={agent.name} className="w-48 h-64 rounded-xl object-cover" />
                    <div className="max-h-64 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                        <h1 className="text-3xl font-bold mb-1">{agent.name}</h1>
                        <div className="text-gray-500 mb-3">{agent.title}</div>
                        <p className="text-gray-700 leading-relaxed max-w-100 whitespace-pre-line">
                            {(() => {
                                try {
                                    if (!agent.description) return "";
                                    const desc = JSON.parse(agent.description);
                                    const norwegian = desc.norwegian || "";

                                    // ✅ Strip tags
                                    const cleanDesc = norwegian.replace(/<[^>]*>/g, "");
                                    return cleanDesc;
                                } catch (e) {
                                    console.warn("Invalid description JSON:", agent.description);
                                    const fallback = typeof agent.description === "string" ? agent.description : "";
                                    return fallback.replace(/<[^>]*>/g, "");
                                }
                            })()}
                        </p>
                    </div>
                </div>

                {/* Skills */}
                <Card className="bg-gray-50 border border-gray-200 rounded-2xl mb-6">
                    <CardHeader>
                        <CardTitle className="text-yellow-700">Kompetanseområder</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-wrap gap-3">
                        {agent.skills.map((s, i) => (
                            <button
                                key={i}
                                onClick={() => setSelectedSkill(s)}
                                className="flex items-center gap-2 bg-white hover:bg-yellow-50 text-gray-800 border border-gray-200 px-4 py-2 rounded-xl transition transform hover:scale-105 hover:shadow-md duration-300"
                            >
                                {s.icon}
                                {s.name}
                            </button>
                        ))}
                    </CardContent>
                </Card>

                {/* Contact */}
                <Card className="bg-gray-50 border border-gray-200 rounded-2xl mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-yellow-700">Kontakt og booking</CardTitle>
                        <div className="text-yellow-600 font-medium">Fra {agent.customer_minute_price} kr/min</div>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <button className="bg-gray-100 py-3 rounded-xl flex flex-col items-center text-sm transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                            <Phone className="mb-1 text-green-500" /> Ring
                        </button>
                        <button className="bg-gray-100 py-3 rounded-xl flex flex-col items-center text-sm transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                            <Video className="mb-1 text-green-500" /> Video
                        </button>
                        <button className="bg-gray-100 py-3 rounded-xl flex flex-col items-center text-sm transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                            <MessageCircle className="mb-1 text-green-500" /> Chat
                        </button>
                        <button
                            onClick={() => setShowBooking(true)}
                            className="bg-gray-100 py-3 rounded-xl flex flex-col items-center text-sm hover:bg-yellow-50 transform transition-all duration-300 hover:scale-105 hover:shadow-md"
                        >
                            <Calendar className="mb-1 text-gray-500" /> Book
                        </button>
                    </CardContent>
                </Card>

                {showBooking && <BookingSelector agent={agent} onClose={() => setShowBooking(false)} />}
                {selectedSkill && <SkillModal skill={selectedSkill} onClose={() => setSelectedSkill(null)} />}
            </div>)}

            {/* Footer */}
            <Footer />
        </div>
    );
}
